<template>
  <canvas :class="{ hide: isEmpty, [canvasClass]: canvasClass, ship_height: diagram }" :width="width" :height="height" ref="canvas"></canvas>
  <div v-if="isEmpty" :class="`empty-map typo-tiny ${emptyClass}`">
    <div class="empty-map__text">
      {{ emptyText }}
    </div>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onUpdated, onMounted, ref } from 'vue';

import { projectWebMercator, rgeoToPolygon, rgeoToLatLng } from '@/functions/geospatial_conversions';
import SimpleProjection from '@/functions/map_preview/simple_projection';
import WorldProjection from '@/functions/map_preview/world_projection';
import { ARCGIS_BASE_URL, DEFAULT_POINT_ZOOM } from '@/vendor/leaflet_mods';

const TSIZE = 256;

const props = defineProps({
  polygon: { type: String, default: null },
  point: { type: String, default: null },
  emptyText: { type: String, required: true },
  canvasClass: { type: String, default: '' },
  emptyClass: { type: String, default: '' },
  diagram: { type: String, required: false, default: null },
});

const canvas = ref(null);
const width = ref(0);
const height = ref(0);
const diagramMetadata = ref(null);
const projection = props.diagram ? new SimpleProjection(props.diagram) : new WorldProjection();

const isEmpty = computed(() => {
  return !props.point && !props.polygon;
});

let ctx = null;
onMounted(async () => {
  ctx = canvas.value.getContext('2d');
  await projection.init();
  update();

  window.addEventListener('resize', onResize, { passive: true });
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', onResize);
});

function getPolygon() {
  if (!props.polygon) return null;

  try {
    return rgeoToPolygon(props.polygon);
  } catch (e) {
    console.error(e);
    return null;
  }
}

function getPoint() {
  if (!props.point) return null;

  try {
    return rgeoToLatLng(props.point);
  } catch (e) {
    console.error(e);
    return null;
  }
}

function onResize() {
  if (
    canvas.value &&
    (
      width.value !== canvas.value.scrollWidth ||
      width.value !== canvas.value.scrollWidth
    )
  ) {
    update();
  }
}

async function update() {
  const zoomOut = props.diagram ? 3 : 1;
  if (canvas.value) {
    width.value = canvas.value.scrollWidth * zoomOut;
    height.value = canvas.value.scrollHeight * zoomOut;
  }

  let pcx, pcy, zoom;

  if (props.polygon) {
    let bounds = projection.calcBounds(getPolygon());
    let center = projection.calcCenter(bounds);
    [pcx, pcy] = projection.project(...center);
    zoom = getBoundsZoom(bounds, 32);
  }
  else if (props.point) {
    [pcx, pcy] = projection.project(...getPoint());
    zoom = projection.pointZoom();
  } else {
    return;
  }

  let cx = pcx * TSIZE;
  let cy = pcy * TSIZE;

  ctx.fillStyle = 'white';
  ctx.clearRect(0, 0, width.value, height.value);
  let snapZoom = Math.ceil(zoom);
  let [tilesx, tilesy] = projection.tilesAtZoom(snapZoom);
  let scale = projection.zoomToScale(zoom);
  let snapScale = projection.zoomToScale(snapZoom);
  let tileScale = scale / snapScale;
  let csx = cx * scale;
  let csy = cy * scale;

  let tlx = csx - width.value / 2;
  let tly = csy - height.value / 2;

  let tlsx = tlx / TSIZE / tileScale;
  let tlsy = tly / TSIZE / tileScale;
  let brsx = (csx + width.value / 2) / TSIZE / tileScale;
  let brsy = (csy + height.value / 2) / TSIZE / tileScale;

  let starti = Math.max(Math.floor(tlsx), 0);
  let endi = Math.min(Math.ceil(brsx), tilesx);

  let startj = Math.max(Math.floor(tlsy), 0);
  let endj = Math.min(Math.ceil(brsy), tilesy);

  let drawPromises = [];
  ctx.fillStyle = '#f0f';
  for (let i = starti; i < endi; i++) {
    for (let j = startj; j < endj; j++) {
      let [tx, ty] = projection.tileCoord(i, j, zoom);

      let x = tx - tlx;
      let y = ty - tly;

      drawPromises.push(drawTile(
        snapZoom,
        j, i,
        x, y,
        TSIZE * tileScale, TSIZE * tileScale));
    }
  }

  await Promise.all(drawPromises);

  ctx.strokeStyle = '#FFFFFF';
  ctx.fillStyle = 'rgba(54, 53, 54, 0.2)';
  if (props.polygon) {
    let scaledPoints = getPolygon().map(s => s.map((p) => {
      let [x, y] = projection.project(...p);

      return [
        x * TSIZE * scale - tlx,
        y * TSIZE * scale - tly,
      ];
    }));

    scaledPoints.forEach(s => {
      ctx.beginPath();
      ctx.moveTo(s[0][0], s[0][1]);
      for (let i = 1; i < s.length; i++) {
        let [x, y] = s[i];
        ctx.lineTo(x, y);
      }
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
    });
  }
}

onUpdated(update);

function fetchTile (z, x, y) {
  return new Promise(resolve => {
    let img = document.createElement('img');
    img.onload = function () {
      resolve(img);
    }
    img.src = projection.tileUrl(z, x, y);
  });
}

async function drawTile(z, x, y, dx, dy, dw, dh) {
  let tile = await fetchTile(z, x, y);
  ctx.drawImage(tile, dx, dy, dw, dh);
}

/**
 * @param param0 {[[number, number], [number, number]]}
 * @param padding {number}
 * @param fromZoom {number}
 */
function getBoundsZoom([nw, se], padding, fromZoom = 0) {
  let tl = projection.project(...nw).map(p => p * TSIZE * projection.zoomToScale(fromZoom));
  let br = projection.project(...se).map(p => p * TSIZE * projection.zoomToScale(fromZoom));
  let bw = Math.abs(br[0] - tl[0]);
  let bh = Math.abs(br[1] - tl[1]);
  let scalex = (width.value - padding * 2) / bw;
  let scaley = (height.value - padding * 2) / bh;
  let scale = Math.min(scalex, scaley);

  return Math.min(17, projection.getScaleZoom(scale, fromZoom));
}
</script>
